import classNames from 'classnames'
import {
  ArrowRightSmIcon,
  ColumnSkeleton,
  LoadingSpinner,
  LogoutIcon,
  RenderIf,
} from 'components/Basic'
import {colorCodes} from 'definitions/constant'
import {selectUser} from 'features/User'
import {RoleNames, SliceStatus, ValidUserTypes} from 'interfaces'
import {FC} from 'react'
import {useSelector} from 'react-redux'

interface SwitchAccountProps {
  selectedTenant: string
  onAccountSwitch: (e: React.MouseEvent<HTMLElement>, value: string) => void
  isTopBarToBeBlueColor: boolean
}

const SwitchAccount: FC<SwitchAccountProps> = ({
  selectedTenant,
  onAccountSwitch,
  isTopBarToBeBlueColor,
}) => {
  const {primaryDomain, multiAccount, tokenStatus} = useSelector(selectUser)

  return (
    <>
      <div
        className="text-sm pt-4 mb-1 px-6 font-normal flex cursor-pointer w-full justify-start items-center"
        onClick={e => e.stopPropagation()}
      >
        <p id="name" className="karla cursor-pointer ">
          Switch Account
        </p>
      </div>
      <section className="pb-1">
        {multiAccount?.details?.listOfDomains?.length ? (
          <div
            className="overflow-y-auto overflow-x-hidden max-h-32"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-1 flex justify-start items-center">
              <p
                className="karla text-xs font-normal text-white"
                data-cy="current-user-name"
              >
                <span className="text-gray-1100">{primaryDomain}</span>
                {'  '}
                <span className="text-green-100">(current)</span>
              </p>
              <ArrowRightSmIcon
                classes="ml-auto"
                strokeColor={colorCodes.gray1100}
              />
            </div>
            <div className="mx-6 border-b-1 border-gray-1200"></div>
            {multiAccount?.details?.listOfDomains
              ?.filter(item => item !== primaryDomain)
              .map(tenant => (
                <>
                  <div
                    className={classNames(
                      'px-6 py-1 flex cursor-pointer justify-between items-center',
                      {
                        'hover:bg-gray-800': !isTopBarToBeBlueColor,
                        'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
                      },
                    )}
                    onClick={e => onAccountSwitch(e, tenant)}
                  >
                    <p
                      className="karla pl-px text-xs text-white underline decoration-0"
                      data-cy="switch-account-tenant"
                    >
                      {tenant}
                    </p>
                    {tokenStatus === SliceStatus.pending &&
                    selectedTenant === tenant ? (
                      <span
                        className="ml-auto pb-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <LoadingSpinner
                          type="TailSpin"
                          color="#FFFFFF"
                          height={16}
                          width={16}
                        />
                      </span>
                    ) : (
                      <ArrowRightSmIcon classes="ml-auto cursor-pointer" />
                    )}
                  </div>
                  <div className="mx-6 border-t-1 border-gray-1200"></div>
                </>
              ))}
          </div>
        ) : (
          <>
            <div className="mx-6 pt-2 pb-1 flex cursor-pointer justify-start items-center border-b-1 border-gray-1200">
              <ColumnSkeleton width={200} classes="mb-2" />
            </div>
            <div className="mx-6 pt-2 pb-1 flex cursor-pointer justify-start items-center border-b-1 border-gray-1200">
              <ColumnSkeleton width={200} classes="mb-2" />
            </div>
          </>
        )}
      </section>
    </>
  )
}

interface LogoutContainerProps {
  name: string
  pathname: string
  emailId: string
  selectedTenant: string
  onLogout: (e: React.MouseEvent<HTMLElement>) => void
  onAccountSwitch: (e: React.MouseEvent<HTMLElement>, value: string) => void
  isTopBarToBeBlueColor: boolean
}

export const LogoutContainer: FC<LogoutContainerProps> = ({
  name,
  pathname,
  emailId,
  selectedTenant,
  onLogout,
  onAccountSwitch,
  isTopBarToBeBlueColor,
}) => {
  const {role, multiAccount} = useSelector(selectUser)

  return (
    <div
      id="logout"
      className={classNames(
        'py-4 border-l-0.5 border-b-0.5 border-r-0.5 border-gray-400 w-auto min-w-96 z-50 absolute right-6',
        pathname !== '/policies' && 'col-span-4 col-start-11',
        {
          'bg-dope-black': !isTopBarToBeBlueColor,
          'bg-dope-teal-100': isTopBarToBeBlueColor,
        },
      )}
    >
      <p id="name" className="text-sm karla px-6">
        {name}
        <span id="email" className="text-gray-1100">
          &nbsp;({emailId})
        </span>
        <RenderIf
          isTrue={[ValidUserTypes.User, ValidUserTypes.Analyst].includes(role)}
        >
          <>
            &nbsp;&nbsp;
            <span className="text-gray-1100 px-2 border-0.5 rounded-full border-gray-1100">
              {
                {
                  [ValidUserTypes.User]: RoleNames['Read-Only'],
                  [ValidUserTypes.Analyst]: RoleNames.Analyst,
                }[role]
              }
            </span>
          </>
        </RenderIf>
      </p>
      <div
        data-cy="logout-action"
        className={classNames('mt-1 pt-1 px-6 cursor-pointer', {
          'hover:bg-gray-800': !isTopBarToBeBlueColor,
          'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
        })}
        onClick={onLogout}
      >
        <div className="flex justify-start items-center pb-1">
          <LogoutIcon />
          <p className="pl-1 karla text-xs text-white underline decoration-0">
            Log Out
          </p>
          <ArrowRightSmIcon classes="ml-auto cursor-pointer" />
        </div>
        <div className="border-b-1 border-gray-1200 px-12"></div>
      </div>
      <RenderIf isTrue={multiAccount?.details?.listOfDomains?.length > 1}>
        <SwitchAccount
          selectedTenant={selectedTenant}
          onAccountSwitch={onAccountSwitch}
          isTopBarToBeBlueColor={isTopBarToBeBlueColor}
        />
      </RenderIf>
    </div>
  )
}
